import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { fmtMoney } from "../lib/format";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Trash2, Crown, User as UserIcon, Save, ChevronDown, ChevronUp, Wallet, Package, Eraser, LogIn } from "lucide-react";
import HelpButton from "../components/HelpButton";

// Human-friendly labels + short descriptions used for tooltips and toggle text.
const MODULES = {
  forecast: {
    label: "Forecast",
    hint: "2-week projected balance from recurring bills, paychecks, and transfers.",
  },
  multi_account: {
    label: "Multi-Account",
    hint: "Track checking, savings, and cash side-by-side with per-account reconcile.",
  },
  sms: {
    label: "Text-in (SMS)",
    hint: "Add transactions by texting the Twilio number. Also unlocks balance & recent digests.",
  },
  csv_import: {
    label: "CSV Import",
    hint: "Upload a bank statement CSV to bulk-import and auto-match transactions.",
  },
  duplicates: {
    label: "Duplicates",
    hint: "Find and merge duplicate transactions (fuzzy match on payee/date/amount).",
  },
  budgets: {
    label: "Budgets",
    hint: "Set per-category monthly budgets and see progress on the dashboard.",
  },
  receipt_ocr: {
    label: "Receipt OCR",
    hint: "Snap a photo of a receipt to auto-fill amount, payee, and category.",
  },
  smart_budgets: {
    label: "Smart Budgets",
    hint: "Auto-suggest per-category budgets from the last 3 months of spending.",
  },
  savings_goals: {
    label: "Savings Goals",
    hint: "Named goals with progress bars and auto-detect when a target is reached.",
  },
  receipt_ocr_quota: {
    label: "Unlimited OCR",
    hint: "Remove the 20-scan monthly free-tier cap on receipt OCR.",
  },
  bulk_templates: {
    label: "Bulk-add Templates",
    hint: "Save common bulk-entry lineups (rent, weekly bills, allowance) and re-load them with one tap.",
  },
};
// Back-compat alias for existing references
const MODULE_LABELS = Object.fromEntries(
  Object.entries(MODULES).map(([k, v]) => [k, v.label])
);

// Preset bundles for one-click plan assignment. Each maps to the set of PAID
// modules that should be enabled. Free modules (not in PAID_MODULES) are
// always on regardless of these presets.
// 2-tier plan (Free / Pro) — Family was removed 2026-02 (no unique features).
const PRESETS = [
  { key: "free", label: "Free", modules: [] },
  {
    key: "pro",
    label: "Pro",
    modules: ["forecast", "multi_account", "sms", "smart_budgets", "savings_goals", "receipt_ocr_quota", "bulk_templates"],
  },
];

export default function Admin() {
  const { user: currentUser, beginImpersonation } = useAuth();
  const paidModules = currentUser?.paid_modules || ["forecast", "multi_account"];
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState({}); // uid -> partial fields
  const [expanded, setExpanded] = useState({}); // uid -> bool
  const [accounts, setAccounts] = useState({}); // uid -> array
  const [accountEdits, setAccountEdits] = useState({}); // account_id -> partial
  // Consolidated wipe modal — one prompt covers all wipe targets + scopes.
  // Shape: { target: "user"|"account", usr, acc?, scope: "all"|"imported" } | null
  const [wipeChoice, setWipeChoice] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Not authorized");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const toggleExpand = async (uid) => {
    const next = !expanded[uid];
    setExpanded({ ...expanded, [uid]: next });
    if (next && !accounts[uid]) {
      const { data } = await api.get(`/admin/users/${uid}/accounts`);
      setAccounts({ ...accounts, [uid]: data });
    }
  };

  const setAccField = (aid, k, v) =>
    setAccountEdits({ ...accountEdits, [aid]: { ...accountEdits[aid], [k]: v } });

  const saveAccount = async (uid, aid) => {
    const patch = accountEdits[aid] || {};
    if (Object.keys(patch).length === 0) return;
    try {
      await api.put(`/admin/accounts/${aid}`, patch);
      toast.success("Account updated");
      setAccountEdits({ ...accountEdits, [aid]: {} });
      const { data } = await api.get(`/admin/users/${uid}/accounts`);
      setAccounts({ ...accounts, [uid]: data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteAccount = async (uid, acc) => {
    if (!window.confirm(`Delete bank account "${acc.name}"? Its transactions will be reassigned to another account.`)) return;
    try {
      await api.delete(`/admin/accounts/${acc.id}`);
      toast.success(`Deleted ${acc.name}`);
      const { data } = await api.get(`/admin/users/${uid}/accounts`);
      setAccounts({ ...accounts, [uid]: data });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const wipeAdminAccount = async (uid, acc, importedOnly) => {
    const scope = importedOnly ? "CSV-imported" : "ALL";
    try {
      const { data } = await api.delete(
        `/admin/accounts/${acc.id}/transactions${importedOnly ? "?imported_only=true" : ""}`
      );
      toast.success(`Wiped ${data.deleted} ${scope} txn${data.deleted === 1 ? "" : "s"} from ${acc.name}`);
      const { data: fresh } = await api.get(`/admin/users/${uid}/accounts`);
      setAccounts({ ...accounts, [uid]: fresh });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    }
  };

  // Unified wipe executor — dispatches to the correct backend endpoint based
  // on target ("user"|"account") + scope ("all"|"imported"). Called by the
  // WipeModal confirm button so all confirmation logic lives in one place.
  const confirmWipe = async () => {
    if (!wipeChoice) return;
    const { target, usr, acc, scope } = wipeChoice;
    setWipeChoice(null);
    if (target === "user") {
      if (scope === "all") return wipeTxns(usr);
      return wipeImported(usr);
    }
    return wipeAdminAccount(usr.id, acc, scope === "imported");
  };

  const setField = (uid, k, v) => setEditing({ ...editing, [uid]: { ...editing[uid], [k]: v } });

  const save = async (uid) => {
    const patch = editing[uid] || {};
    if (Object.keys(patch).length === 0) return;
    try {
      await api.put(`/admin/users/${uid}`, patch);
      toast.success("User updated");
      setEditing({ ...editing, [uid]: {} });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const togglePlan = async (usr) => {
    const next = usr.plan === "paid" ? "free" : "paid";
    await api.put(`/admin/users/${usr.id}`, { plan: next });
    toast.success(`${usr.name} → ${next}`);
    load();
  };

  const remove = async (usr) => {
    if (!window.confirm(`Delete ${usr.email} and all their data?`)) return;
    try {
      await api.delete(`/admin/users/${usr.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const wipeTxns = async (usr) => {
    try {
      const { data } = await api.delete(`/admin/users/${usr.id}/transactions`);
      toast.success(`Deleted ${data.deleted} transaction${data.deleted === 1 ? "" : "s"} for ${usr.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    }
  };

  const wipeImported = async (usr) => {
    try {
      const { data } = await api.delete(`/admin/users/${usr.id}/transactions/imported`);
      toast.success(`Deleted ${data.deleted} imported txn${data.deleted === 1 ? "" : "s"} for ${usr.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    }
  };

  const nav = useNavigate();
  const impersonate = async (usr) => {
    if (usr.is_admin) {
      toast.error("Cannot impersonate another admin");
      return;
    }
    if (!window.confirm(
      `Impersonate ${usr.name} (${usr.email})?\n\n` +
      `You will see the app as this user for up to 2 hours. A red banner will show at the top so you can return to your admin session anytime.`
    )) return;
    try {
      const { data } = await api.post(`/admin/impersonate/${usr.id}`);
      await beginImpersonation(data.access_token);
      toast.success(`Now viewing as ${data.target.name}`);
      nav("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation failed");
    }
  };

  const toggleModule = async (usr, moduleName) => {
    // Determine current effective enabled paid modules for this user
    const eff = usr.effective_modules || {};
    const currentEnabled = paidModules.filter((m) => eff[m]);
    const has = currentEnabled.includes(moduleName);
    const next = has ? currentEnabled.filter((m) => m !== moduleName) : [...currentEnabled, moduleName];
    try {
      await api.put(`/admin/users/${usr.id}`, { enabled_modules: next });
      toast.success(`${MODULE_LABELS[moduleName] || moduleName} ${has ? "disabled" : "enabled"} for ${usr.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const applyPreset = async (usr, preset) => {
    // Only send paid modules the server knows about
    const next = preset.modules.filter((m) => paidModules.includes(m));
    if (!window.confirm(
      `Apply "${preset.label}" preset to ${usr.name}?\n\n` +
      `Paid modules enabled: ${next.map((m) => MODULE_LABELS[m]).join(", ") || "none (free tier)"}.`
    )) return;
    try {
      await api.put(`/admin/users/${usr.id}`, { enabled_modules: next });
      toast.success(`Applied ${preset.label} to ${usr.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preset failed");
    }
  };

  // Determine which preset key currently matches a user's enabled paid modules
  const activePresetKey = (usr) => {
    const eff = usr.effective_modules || {};
    const current = new Set(paidModules.filter((m) => eff[m]));
    for (const p of PRESETS) {
      const wanted = new Set(p.modules.filter((m) => paidModules.includes(m)));
      if (
        current.size === wanted.size &&
        [...current].every((m) => wanted.has(m))
      ) {
        return p.key;
      }
    }
    return null;
  };

  return (
    <div className="space-y-4" data-testid="admin-page">
      <div className="flex items-center justify-between">
        <Link to="/" className="nb-btn nb-btn-secondary !py-2 !px-3">
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Admin</h1>
        <HelpButton id="admin" />
      </div>

      <div className="nb-card p-4 text-sm font-bold">
        {users.length} user{users.length === 1 ? "" : "s"} · {users.filter((x) => x.plan === "paid").length} paid ·{" "}
        {users.filter((x) => x.is_admin).length} admin
      </div>

      {users.map((usr) => {
        const patch = editing[usr.id] || {};
        return (
          <div
            key={usr.id}
            data-testid={`admin-user-${usr.email}`}
            className="nb-card p-4 space-y-2"
            style={{ background: usr.plan === "paid" ? "#b5e4ca" : "#fff" }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                {usr.is_admin ? <Crown size={18} strokeWidth={3} /> : <UserIcon size={18} strokeWidth={3} />}
                <div className="min-w-0">
                  <div className="font-display font-black text-lg truncate">{usr.name}</div>
                  <div className="text-xs font-bold text-[#4a4a4a] truncate">{usr.email}</div>
                </div>
              </div>
              <button
                data-testid={`toggle-plan-${usr.email}`}
                onClick={() => togglePlan(usr)}
                className={`chip !text-xs ${usr.plan === "paid" ? "active !bg-[#fdecc8]" : ""}`}
              >
                {usr.plan === "paid" ? "PAID" : "FREE"}
              </button>
            </div>
            <div className="text-xs font-medium text-[#4a4a4a]">
              {usr.transaction_count} txns
              {usr.imported_count > 0 && (
                <span data-testid={`imported-count-${usr.email}`}> ({usr.imported_count} imported)</span>
              )}{" "}
              · {usr.account_count} accounts · start: {fmtMoney(usr.starting_balance)}
            </div>

            <div
              className="pt-1 border-t-2 border-[#1a1a1a]/10"
              data-testid={`modules-panel-${usr.email}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-1">
                  <Package size={12} strokeWidth={3} />
                  <div className="label !mb-0 !text-[10px]">Modules</div>
                </div>
                <div
                  className="flex flex-wrap gap-1"
                  data-testid={`preset-row-${usr.email}`}
                >
                  {PRESETS.map((p) => {
                    const isActive = activePresetKey(usr) === p.key;
                    return (
                      <button
                        key={p.key}
                        data-testid={`preset-${p.key}-${usr.email}`}
                        onClick={() => applyPreset(usr, p)}
                        title={`Apply preset "${p.label}" — ${
                          p.modules.length
                            ? p.modules.map((m) => MODULE_LABELS[m]).join(", ")
                            : "free tier only"
                        }`}
                        className={`chip !text-[10px] !py-0.5 !px-1.5 ${isActive ? "active !bg-[#fdecc8]" : ""}`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {paidModules.map((m) => {
                  const enabled = usr.effective_modules?.[m] === true;
                  const meta = MODULES[m] || { label: m, hint: "" };
                  return (
                    <button
                      key={m}
                      data-testid={`module-toggle-${usr.email}-${m}`}
                      onClick={() => toggleModule(usr, m)}
                      className={`chip !text-[11px] !py-1 !px-2 ${enabled ? "active !bg-[#b5e4ca]" : ""}`}
                      title={`${meta.label}\n${meta.hint}\n\n(Click to ${enabled ? "disable" : "enable"})`}
                    >
                      {enabled ? "✓ " : ""}{meta.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                data-testid={`admin-name-${usr.email}`}
                className="nb-input !py-2"
                placeholder={usr.name}
                value={patch.name || ""}
                onChange={(e) => setField(usr.id, "name", e.target.value)}
              />
              <input
                data-testid={`admin-start-${usr.email}`}
                type="number"
                step="0.01"
                className="nb-input !py-2"
                placeholder={`Start ${usr.starting_balance}`}
                value={patch.starting_balance ?? ""}
                onChange={(e) => setField(usr.id, "starting_balance", parseFloat(e.target.value))}
              />
            </div>
            <input
              data-testid={`admin-pw-${usr.email}`}
              type="password"
              className="nb-input !py-2"
              placeholder="New password (blank = keep)"
              value={patch.password || ""}
              onChange={(e) => setField(usr.id, "password", e.target.value)}
            />
            <div className="flex gap-2">
              <button
                data-testid={`save-user-${usr.email}`}
                onClick={() => save(usr.id)}
                className="nb-btn nb-btn-dark flex-1 !py-2"
              >
                <Save size={14} strokeWidth={3} /> Save
              </button>
              <button
                data-testid={`expand-accounts-${usr.email}`}
                onClick={() => toggleExpand(usr.id)}
                className="nb-btn nb-btn-secondary !py-2 !px-3"
                title="Manage bank accounts"
              >
                <Wallet size={14} strokeWidth={3} />
                {expanded[usr.id] ? <ChevronUp size={14} strokeWidth={3} /> : <ChevronDown size={14} strokeWidth={3} />}
              </button>
              <button
                data-testid={`wipe-user-${usr.email}`}
                onClick={() =>
                  setWipeChoice({ target: "user", usr, scope: "all" })
                }
                disabled={!usr.transaction_count}
                className="nb-btn nb-btn-secondary !py-2 !px-3 !text-xs"
                title={
                  usr.transaction_count
                    ? `Wipe transactions for ${usr.email} — pick scope in the next step`
                    : "No transactions to wipe"
                }
              >
                <Eraser size={14} strokeWidth={3} /> Wipe…
              </button>
              <button
                data-testid={`impersonate-user-${usr.email}`}
                onClick={() => impersonate(usr)}
                disabled={usr.is_admin || usr.id === currentUser?.id}
                className="nb-btn nb-btn-secondary !py-2 !px-3 !text-xs"
                style={{ background: usr.is_admin || usr.id === currentUser?.id ? undefined : "#d9d4f1" }}
                title={
                  usr.is_admin
                    ? "Cannot impersonate another admin"
                    : usr.id === currentUser?.id
                    ? "Cannot impersonate yourself"
                    : `View app as ${usr.name} (2h session)`
                }
              >
                <LogIn size={14} strokeWidth={3} /> As user
              </button>
              <button
                data-testid={`delete-user-${usr.email}`}
                onClick={() => remove(usr)}
                disabled={usr.is_admin}
                className="nb-btn nb-btn-danger !py-2 !px-3"
                title={usr.is_admin ? "Cannot delete admin" : "Delete user"}
              >
                <Trash2 size={14} strokeWidth={3} />
              </button>
            </div>

            {expanded[usr.id] && (
              <div
                className="mt-2 pt-2 border-t-2 border-[#1a1a1a]/20 space-y-2"
                data-testid={`accounts-panel-${usr.email}`}
              >
                <div className="label !mb-0">Bank accounts ({accounts[usr.id]?.length || 0})</div>
                {(accounts[usr.id] || []).map((a) => {
                  const p = accountEdits[a.id] || {};
                  return (
                    <div
                      key={a.id}
                      data-testid={`admin-account-${a.name}`}
                      className="p-2 rounded-lg border-2 border-[#1a1a1a] space-y-2"
                      style={{ background: a.color || "#fff" }}
                    >
                      <div className="flex items-center gap-2">
                        <Wallet size={14} strokeWidth={3} />
                        <div className="font-bold text-sm">{a.name}</div>
                        <div className="text-xs font-medium text-[#4a4a4a] ml-auto">
                          start: {fmtMoney(a.starting_balance)}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          data-testid={`admin-acc-name-${a.name}`}
                          className="nb-input !py-1.5 !text-sm"
                          placeholder={a.name}
                          value={p.name || ""}
                          onChange={(e) => setAccField(a.id, "name", e.target.value)}
                        />
                        <input
                          data-testid={`admin-acc-start-${a.name}`}
                          type="number"
                          step="0.01"
                          className="nb-input !py-1.5 !text-sm"
                          placeholder={`${a.starting_balance}`}
                          value={p.starting_balance ?? ""}
                          onChange={(e) => setAccField(a.id, "starting_balance", parseFloat(e.target.value))}
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          data-testid={`save-account-${a.name}`}
                          onClick={() => saveAccount(usr.id, a.id)}
                          className="nb-btn nb-btn-dark flex-1 !py-1.5 !text-xs"
                        >
                          <Save size={12} strokeWidth={3} /> Save
                        </button>
                        <button
                          data-testid={`admin-wipe-account-${a.name}`}
                          onClick={() =>
                            setWipeChoice({
                              target: "account",
                              usr,
                              acc: a,
                              scope: "all",
                            })
                          }
                          className="nb-btn nb-btn-secondary !py-1.5 !px-2 !text-xs"
                          title={`Wipe transactions in ${a.name} — pick scope in the next step`}
                        >
                          <Eraser size={12} strokeWidth={3} /> Wipe…
                        </button>
                        <button
                          data-testid={`delete-account-${a.name}`}
                          onClick={() => deleteAccount(usr.id, a)}
                          disabled={(accounts[usr.id] || []).length <= 1}
                          className="nb-btn nb-btn-danger !py-1.5 !px-2 !text-xs"
                          title={(accounts[usr.id] || []).length <= 1 ? "User needs at least one account" : "Delete this bank account"}
                        >
                          <Trash2 size={12} strokeWidth={3} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {(accounts[usr.id] || []).length === 0 && (
                  <div className="text-xs font-medium text-[#4a4a4a] text-center py-2">
                    No bank accounts yet.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {wipeChoice && (
        <div
          data-testid="wipe-modal"
          className="fixed inset-0 z-[80] bg-black/40 flex items-end sm:items-center justify-center p-3"
          onClick={() => setWipeChoice(null)}
        >
          <div
            className="nb-card-lg w-full max-w-md p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff" }}
          >
            <div className="font-display font-black text-xl">
              Wipe transactions
            </div>
            <div className="text-sm font-medium text-[#1a1a1a]">
              {wipeChoice.target === "user" ? (
                <>
                  Target: user <b>{wipeChoice.usr.email}</b>
                  <br />
                  <span className="text-[#4a4a4a]">
                    {wipeChoice.usr.transaction_count} total ·{" "}
                    {wipeChoice.usr.imported_count} CSV-imported
                  </span>
                </>
              ) : (
                <>
                  Target: account <b>{wipeChoice.acc.name}</b> ({wipeChoice.usr.email})
                </>
              )}
            </div>

            <div className="space-y-2 pt-1">
              <label
                data-testid="wipe-scope-all"
                className="flex items-start gap-2 p-3 border-2 border-[#1a1a1a] rounded-xl cursor-pointer bg-white"
                style={{
                  background:
                    wipeChoice.scope === "all" ? "#ffd6d6" : "#fff",
                }}
              >
                <input
                  type="radio"
                  name="wipe-scope"
                  checked={wipeChoice.scope === "all"}
                  onChange={() =>
                    setWipeChoice({ ...wipeChoice, scope: "all" })
                  }
                  className="w-4 h-4 mt-0.5"
                />
                <div>
                  <div className="font-black text-sm">Wipe ALL transactions</div>
                  <div className="text-[11px] text-[#4a4a4a] font-medium">
                    Deletes every transaction (manual, OCR, imports). Keeps the{" "}
                    {wipeChoice.target === "user" ? "user" : "account"}, accounts,
                    budgets, recurring bills.
                  </div>
                </div>
              </label>

              <label
                data-testid="wipe-scope-imported"
                className="flex items-start gap-2 p-3 border-2 border-[#1a1a1a] rounded-xl cursor-pointer bg-white"
                style={{
                  background:
                    wipeChoice.scope === "imported" ? "#fdecc8" : "#fff",
                }}
              >
                <input
                  type="radio"
                  name="wipe-scope"
                  checked={wipeChoice.scope === "imported"}
                  onChange={() =>
                    setWipeChoice({ ...wipeChoice, scope: "imported" })
                  }
                  className="w-4 h-4 mt-0.5"
                />
                <div>
                  <div className="font-black text-sm">
                    Wipe CSV-imported only
                  </div>
                  <div className="text-[11px] text-[#4a4a4a] font-medium">
                    Deletes rows added via bank CSV import (memo = &quot;Imported
                    from bank&quot;). Keeps manual + receipt-OCR txns.
                  </div>
                </div>
              </label>
            </div>

            <div className="text-[11px] font-bold uppercase tracking-wider text-[#a00] text-center pt-1">
              ⚠ This cannot be undone
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="wipe-modal-cancel"
                onClick={() => setWipeChoice(null)}
                className="nb-btn nb-btn-secondary !py-3"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="wipe-modal-confirm"
                onClick={confirmWipe}
                className="nb-btn nb-btn-danger !py-3"
              >
                <Eraser size={14} strokeWidth={3} />
                Wipe
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
